import { createMuiTheme, ThemeProvider } from '@material-ui/core';
import { Formik } from 'formik';
import { graphql } from 'gatsby';
import React, { useState } from 'react';
import Typical from 'react-typical';
import tw from 'twin.macro';
import * as yup from 'yup';

import { Button } from '../components/Button';
import { InstagramPostGallery } from '../components/InstagramPostGallery';
import { Layout } from '../components/Layout';
import { SliceWrapper } from '../core-ui/SliceWrapper';
import { Text, TextType } from '../core-ui/Text';
import TextInput from '../core-ui/TextInput';
import { Wrapper } from '../core-ui/Wrapper';
import { InstagramPostsQuery } from '../__generated__/types';

const initialSchema = {
  fullName: '',
  businessName: '',
  email: '',
  phone: '',
  description: '',
};
export const ERRORS = {
  email: {
    invalid: `Uh-oh! Something isn't right here`,
  },
  description: {
    tooShort: `Don't be shy! Tell us more about the exciting project!`,
  },
  default: {
    required: `Required`,
  },
};

const validationSchema = yup.object({
  email: yup
    .string()
    .email(ERRORS.email.invalid)
    .required(ERRORS.default.required),
  businessName: yup.string().required(ERRORS.default.required),
  fullName: yup.string(),
  phone: yup.string(),
  description: yup.string().required(ERRORS.default.required),
});

const theme = createMuiTheme({
  overrides: {
    MuiFormLabel: {
      root: {
        fontFamily: 'WorkSans, sans-serif',
        fontSize: 24,
        width: '133%',
      },
    },
    MuiInputBase: {
      input: {
        fontFamily: 'WorkSans, sans-serif',
        fontWeight: 500,
        fontSize: 16,
      },
      root: {
        ':after': {
          borderBottomColor: '#fc98c2',
        },
      },
      inputMultiline: {
        lineHeight: '32px',
      },
    },
    MuiTextField: {
      root: {
        width: '95%',
      },
    },
  },
  breakpoints: {
    values: {
      md: 992,
    },
  },
  palette: {
    primary: {
      light: '#fc98c2',
      main: '#2d2d2d;',
    },
    error: {
      main: '#FF8C77',
    },
  },
});

const descriptionStyles = {
  width: '98%',
  fontFamily: 'WorkSans',
};

type Props = {
  data: InstagramPostsQuery;
};

const emptyArray = [];

export default ({ data }: Props) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const body = data.prismicPage?.data.body;
  let buttonLabel: string | undefined = undefined;
  body?.forEach((i) => {
    if (i.__typename === 'PrismicPageDataBodyInstagramGallery') {
      buttonLabel = i.primary?.button_label || undefined;
    }
  });
  return (
    <Layout title="Contact">
      <ThemeProvider theme={theme}>
        <SliceWrapper last>
          <Wrapper row css={tw`justify-between flex-wrap mb-120`}>
            <div css={tw`w-full lg:w-6/12`}>
              <Text css={tw`mt-48 lg:mt-0 mb-36`}>WE’RE HERE FOR YOU.</Text>
              <Text type={TextType.h2} css={tw`opacity-30`}>
                Let’s build
              </Text>
              <Text type={TextType.h2} css={tw`opacity-30`}>
                something brilliant!
              </Text>
              <Text type={TextType.h2} css={tw`lg:leading-66 opacity-30`}>
                Reach out to us for
              </Text>
              <AnimatedTypingComponent />
            </div>
            <div css={tw`w-full lg:w-6/12`}>
              {isSubmitted ? (
                <Text type={TextType.h2}>Your form has been submitted!</Text>
              ) : (
                <Formik
                  initialValues={initialSchema}
                  validationSchema={validationSchema}
                  onSubmit={() => {}}
                  validateOnChange={false}
                  validateOnBlur={false}
                >
                  {({ values, errors, handleChange, validateForm }) => {
                    return (
                      <form
                        name="contact-form"
                        method="post"
                        data-netlify="true"
                        onSubmit={async (e) => {
                          e.preventDefault();
                          let err = await validateForm();
                          if (Object.keys(err).length > 0) {
                            return;
                          }
                          fetch('/', {
                            method: 'POST',
                            headers: {
                              'Content-Type':
                                'application/x-www-form-urlencoded',
                            },
                            body: encode({
                              'form-name': 'contact-form',
                              ...values,
                            }),
                          }).then(() => {
                            setIsSubmitted(true);
                          });
                        }}
                        css={tw`lg:ml-60 mt-36 lg:mt-40`}
                      >
                        <div css={tw`flex flex-col lg:flex-row flex-wrap`}>
                          <div css={tw`w-full lg:w-1/2`}>
                            <div css={tw`mb-24 lg:mb-40`}>
                              <TextInput
                                label="Full Name"
                                name="fullName"
                                errorMessage={errors.fullName}
                                value={values.fullName}
                                onChange={handleChange}
                              />
                            </div>
                            <div css={tw`mb-24 lg:mb-40`}>
                              <TextInput
                                label="Email"
                                name="email"
                                errorMessage={errors.email}
                                value={values.email}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          <div css={tw`w-full lg:w-1/2`}>
                            <div css={tw`mb-24 lg:mb-40`}>
                              <TextInput
                                label="Business Name"
                                name="businessName"
                                errorMessage={errors.businessName}
                                value={values.businessName}
                                onChange={handleChange}
                              />
                            </div>
                            <div css={tw`mb-24 lg:mb-40`}>
                              <TextInput
                                label="Phone Number"
                                name="phone"
                                errorMessage={errors.phone}
                                value={values.phone}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <TextInput
                            onChange={handleChange}
                            name="description"
                            value={values.description}
                            errorMessage={errors.description}
                            label="Tell us about your project"
                            multiline
                            rows={3}
                            style={descriptionStyles}
                          />
                        </div>
                        <div css={tw`flex justify-center lg:justify-end`}>
                          <Button
                            label="SEND MESSAGE"
                            css={tw`mt-32`}
                            type="submit"
                          />
                        </div>
                      </form>
                    );
                  }}
                </Formik>
              )}
            </div>
          </Wrapper>
          {data.allInstaNode ? (
            <InstagramPostGallery
              data={
                (data.allInstaNode.nodes as Array<InstagramPostFragment>) ??
                emptyArray
              }
              igLink={data.prismicSiteSettings?.data.instagram_url?.url || ''}
              buttonLabel={buttonLabel}
            />
          ) : null}
        </SliceWrapper>
      </ThemeProvider>
    </Layout>
  );
};

const words = ['custom homes', 'vacation homes', 'collaborations'];

const AnimatedTypingComponent = () => {
  const [] = useState();
  const generatedWordsArr: Array<string | number> = [1000];
  const generatedWords = words.reduce((prev, word) => {
    return [...prev, `${word}.`, 1000];
  }, generatedWordsArr);
  return (
    <Typical
      steps={generatedWords}
      loop={Infinity}
      wrapper="h2"
      css={tw`text-l sm:text-4xl font-medium underline`}
    />
  );
};

const encode = (data: Record<string, string>) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&');
};

export const query = graphql`
  query InstagramPosts {
    prismicPage(uid: { eq: "contact" }) {
      data {
        body {
          ...InstagramGallerySlice
        }
      }
    }
    prismicSiteSettings {
      data {
        instagram_url {
          link_type
          url
          target
        }
      }
    }
  }
`;
